import { Chip } from '@/components/ui/Chip'
import { STATUS_LABELS } from '@/components/judgments/StatusBadge'
import { titleCase } from '@/lib/format'
import type { JudgmentFilters, JudgmentStatus } from '@/types/api'

export interface ActiveFiltersProps {
  filters: JudgmentFilters
  onChange: (update: Partial<JudgmentFilters>) => void
  onToggleArray: <K extends 'status' | 'filingLocation'>(
    key: K,
    value: JudgmentFilters[K][number],
  ) => void
  onReset: () => void
}

/** Removable summary of what is currently narrowing the result set. */
export function ActiveFilters({
  filters,
  onChange,
  onToggleArray,
  onReset,
}: ActiveFiltersProps) {
  const chips: Array<{ key: string; label: string; onRemove: () => void }> = []

  if (filters.q.trim()) {
    chips.push({
      key: 'q',
      label: `“${filters.q.trim()}”`,
      onRemove: () => onChange({ q: '' }),
    })
  }
  filters.status.forEach((status) =>
    chips.push({
      key: `status-${status}`,
      label: STATUS_LABELS[status as JudgmentStatus] ?? status,
      onRemove: () => onToggleArray('status', status),
    }),
  )
  filters.filingLocation.forEach((location) =>
    chips.push({
      key: `location-${location}`,
      label: titleCase(location),
      onRemove: () => onToggleArray('filingLocation', location),
    }),
  )
  if (filters.partyRole) {
    chips.push({
      key: 'role',
      label: titleCase(filters.partyRole),
      onRemove: () => onChange({ partyRole: null }),
    })
  }
  if (filters.dateFrom) {
    chips.push({
      key: 'from',
      label: `From ${filters.dateFrom}`,
      onRemove: () => onChange({ dateFrom: '' }),
    })
  }
  if (filters.dateTo) {
    chips.push({
      key: 'to',
      label: `To ${filters.dateTo}`,
      onRemove: () => onChange({ dateTo: '' }),
    })
  }
  if (filters.amountMin) {
    chips.push({
      key: 'min',
      label: `≥ ${filters.amountMin}`,
      onRemove: () => onChange({ amountMin: '' }),
    })
  }
  if (filters.amountMax) {
    chips.push({
      key: 'max',
      label: `≤ ${filters.amountMax}`,
      onRemove: () => onChange({ amountMax: '' }),
    })
  }
  if (filters.hasDetails !== null) {
    chips.push({
      key: 'details',
      label: filters.hasDetails ? 'Detail read' : 'Detail not read',
      onRemove: () => onChange({ hasDetails: null }),
    })
  }
  if (filters.captureId !== null) {
    chips.push({
      key: 'capture',
      label: `Capture #${filters.captureId}`,
      onRemove: () => onChange({ captureId: null }),
    })
  }

  if (chips.length === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-2xs font-semibold tracking-[0.06em] text-ink-muted uppercase">
        Filtered by
      </span>
      {chips.map((chip) => (
        <Chip key={chip.key} label={chip.label} onRemove={chip.onRemove} />
      ))}
      {chips.length > 1 ? (
        <button
          type="button"
          onClick={onReset}
          className="ml-1 rounded-sm text-xs font-medium text-accent-text underline-offset-2 hover:underline"
        >
          Clear all
        </button>
      ) : null}
    </div>
  )
}
